import { BaseModule, IModuleConfig } from '@kta20-core/interfaces/base-module';
import {IAppContext} from "@kta20-core/interfaces/i-app-context";
import {ModuleViews} from "@kta20-knowledge-base/views/module-views";
import {RecordTypeConfig} from "@kta20-knowledge-base/models/record-type-config";
import {I_AUTH_BRIDGE, IAuthBridge} from "@kta20-core/interfaces/auth/i-auth-bridge";
import {BridgeManager} from "@kta20-core/bridge-manager";
import {KbService} from "@kta20-knowledge-base/services/kb-service";
import {AuthService} from "@kta20-auth-by-kt1/services/auth-service";
import {AuthBridge} from "@kta20-auth-by-kt1/bridges/auth-bridge";
import {Storage} from "@kta20-core/data/storage";
import {AuthServiceEvents} from "@kta20-core/auth/auth-service-events";
import {
    KB_SEARCH_KEY,
    KB_SEARCH_RECORD_DETAIL,
    KB_SEARCH_RECORDS,
    MODULE_CLASS_NAME,
    ViewNames
} from "@kta20-knowledge-base/constants";
import {NavigationManager} from "@kta20-ui-base/navigation-manager";
import {CustomError} from "@kta20-core/errors/custom-error";


export class KnowledgeBaseModuleConfig implements IModuleConfig {

    name:string=MODULE_CLASS_NAME;

    /**
     *
     * REST entry point to get/send data for this module
     * @param serviceApiUrl
     *
     * Kind of filter option of kb records displayed on UI. (See templates)
     * @param recordTypeConfigs
     */
    constructor(public serviceApiUrl:string, public recordTypeConfigs:RecordTypeConfig[]) {
    }
}

export class KnowledgeBaseModule extends BaseModule {

    static CLASS_NAME = MODULE_CLASS_NAME;

    static async factory(appContext:IAppContext,moduleConfig:KnowledgeBaseModuleConfig):Promise<BaseModule> {

        appContext;

        if(moduleConfig.recordTypeConfigs.length===0)
            throw new CustomError('RecordTypeConfig in Knowledgebase module configuration cannot be empty');

        //gets a concrete class from config since
        //there might be multiple implementations (web/mob)
        NavigationManager.get().addViews(ModuleViews);

        //uses auth to configure kb-service
        const authService = AuthService.get();

        //initialize the auth service & make public by bridge
        //if not already done from other modules
        const authBridge = AuthBridge.factory(authService);
        BridgeManager.get().addBridge(I_AUTH_BRIDGE,authBridge);

        //initiate the KbSearchService by passing authBridge
        //so he knows how to get the auth token to make requests
        const kbService = <KbService>KbService.get<typeof KbService>(
            moduleConfig.serviceApiUrl,
            (<IAuthBridge>authBridge).getToken.bind(authBridge)
        );

        kbService.cacheStorage = new Storage();

        //remove cached data from previous app run (if any)
        (await KbService.get().cacheStorage.match(KB_SEARCH_RECORD_DETAIL))
                .forEach(async it=>await KbService.get().cacheStorage.removeItem(it));

        await KbService.get().cacheStorage.removeItem(KB_SEARCH_RECORDS);
        await KbService.get().cacheStorage.removeItem(KB_SEARCH_KEY);

        //remove cached data when user session ends so no risk
        //that a user with different permissions see cached
        //data
        authService.addEventListener(AuthServiceEvents.SESSION_ABOUT_TO_EXPIRE,async ()=>{
            (await KbService.get().cacheStorage.match(KB_SEARCH_RECORD_DETAIL))
                .forEach(async it=>await KbService.get().cacheStorage.removeItem(it));

            await KbService.get().cacheStorage.removeItem(KB_SEARCH_RECORDS);
            await KbService.get().cacheStorage.removeItem(KB_SEARCH_KEY);
        });

        authService.watchExpiration();

        //preloading since will be included into KT1 chatbox and
        //and the view must be ready for it.
        await NavigationManager.get().loadView(ViewNames.KB_DEMO_CARD_VIEW);

        return new KnowledgeBaseModule;
    };
}
